import { type getCollectionProps, useInputControl } from '@conform-to/react';
import { InputMask, type InputMaskProps } from '@react-input/mask';
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from 'input-otp';
import React, { useId } from 'react';
import { Checkbox, type CheckboxProps } from '#app/components/ui/checkbox.tsx';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '#app/components/ui/input-otp.tsx';
import { Input } from '#app/components/ui/input.tsx';
import { Label } from '#app/components/ui/label.tsx';
import { RadioGroup, RadioIndicator, RadioItem, type RadioProps } from '#app/components/ui/radio.tsx';
import { Textarea } from '#app/components/ui/textarea.tsx';
import { cn } from '#app/utils/misc.js';
import { InputAddress, type InputAddressProps } from './ui/input-address';

export type ListOfErrors = Array<string | null | undefined> | null | undefined;

export function ErrorList({ id, errors }: { errors?: ListOfErrors; id?: string }) {
  const errorsToRender = errors?.filter(Boolean);
  if (!errorsToRender?.length) return null;
  return (
    <ul id={id} className="flex flex-col gap-1">
      {errorsToRender.map((e) => (
        <li key={e} className="text-[10px] text-red-500">
          {e}
        </li>
      ))}
    </ul>
  );
}

export function Field({
  labelProps,
  inputProps,
  inputMaskProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  inputMaskProps?: InputMaskProps;
  errors?: ListOfErrors;
  className?: string;
}) {
  const fallbackId = useId();
  const id = inputProps.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;
  return (
    <div className={className}>
      <Label htmlFor={id} {...labelProps} />
      {inputMaskProps?.mask ? (
        <InputMask
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          {...inputProps}
          {...inputMaskProps}
          component={Input}
        />
      ) : (
        <Input id={id} aria-invalid={errorId ? true : undefined} aria-describedby={errorId} {...inputProps} />
      )}
      {errorId ? (
        <div className="min-h-[32px] px-2 pb-3 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      ) : (
        <div className="mb-2" />
      )}
    </div>
  );
}

export function OTPField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  inputProps: Partial<OTPInputProps & { render: never }>;
  errors?: ListOfErrors;
  className?: string;
}) {
  const fallbackId = useId();
  const id = inputProps.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;
  return (
    <div className={className}>
      <Label htmlFor={id} {...labelProps} />
      <InputOTP
        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
        inputMode="text"
        maxLength={6}
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...inputProps}
      >
        <InputOTPGroup>
          <InputOTPSlot index={0} />
          <InputOTPSlot index={1} />
          <InputOTPSlot index={2} />
        </InputOTPGroup>
        <InputOTPSeparator />
        <InputOTPGroup>
          <InputOTPSlot index={3} />
          <InputOTPSlot index={4} />
          <InputOTPSlot index={5} />
        </InputOTPGroup>
      </InputOTP>
      <div className="min-h-[32px] px-4 pb-2 pt-1">{errorId ? <ErrorList id={errorId} errors={errors} /> : null}</div>
    </div>
  );
}

export function AddressField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  inputProps: InputAddressProps;
  errors?: ListOfErrors;
  className?: string;
}) {
  const fallbackId = useId();
  const id = inputProps.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;
  return (
    <div className={className}>
      <Label htmlFor={id} {...labelProps} />
      <InputAddress id={id} aria-invalid={errorId ? true : undefined} aria-describedby={errorId} {...inputProps} />
      {errorId ? (
        <div className="min-h-[32px] px-2 pb-3 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      ) : (
        <div className="mb-2" />
      )}
    </div>
  );
}

export function TextareaField({
  labelProps,
  textareaProps,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>;
  textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  errors?: ListOfErrors;
  className?: string;
}) {
  const fallbackId = useId();
  const id = textareaProps.id ?? textareaProps.name ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;
  return (
    <div className={className}>
      <Label htmlFor={id} {...labelProps} />
      <Textarea id={id} aria-invalid={errorId ? true : undefined} aria-describedby={errorId} {...textareaProps} />
      {errorId ? (
        <div className="min-h-[32px] px-2 pb-3 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      ) : (
        <div className="mb-2" />
      )}
    </div>
  );
}

export function CheckboxField({
  labelProps,
  buttonProps,
  errors,
  className,
}: {
  labelProps: JSX.IntrinsicElements['label'];
  buttonProps: CheckboxProps & {
    name: string;
    form: string;
    value?: string;
  };
  errors?: ListOfErrors;
  className?: string;
}) {
  const { key, defaultChecked, ...checkboxProps } = buttonProps;
  const fallbackId = useId();
  const checkedValue = buttonProps.value ?? 'on';
  const input = useInputControl({
    key,
    name: buttonProps.name,
    formId: buttonProps.form,
    initialValue: defaultChecked ? checkedValue : undefined,
  });
  const id = buttonProps.id ?? fallbackId;
  const errorId = errors?.length ? `${id}-error` : undefined;

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <Checkbox
          {...checkboxProps}
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          checked={input.value === checkedValue}
          onCheckedChange={(state) => {
            input.change(state.valueOf() ? checkedValue : '');
            buttonProps.onCheckedChange?.(state);
          }}
          onFocus={(event) => {
            input.focus();
            buttonProps.onFocus?.(event);
          }}
          onBlur={(event) => {
            input.blur();
            buttonProps.onBlur?.(event);
          }}
          type="button"
        />
        <label
          htmlFor={id}
          {...labelProps}
          className={cn('text-muted-foreground', checkboxProps.disabled && 'opacity-50', labelProps.className)}
        />
      </div>
      {errorId ? (
        <div className="px-4 pb-3 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      ) : (
        <div className="mb-2" />
      )}
    </div>
  );
}

export type RadioFieldRenderItemProps = {
  radioProps: Omit<ReturnType<typeof getCollectionProps>[number], 'name' | 'type' | 'key'>;
  label: string;
  index: number;
  fullProp: ReturnType<typeof getCollectionProps>[number];
};

export function RadioField({
  groupProps,
  collectionProps,
  labels,
  errors,
  className = `flex flex-col justify-end gap-2`,
  renderItem,
}: {
  groupProps: RadioProps;
  collectionProps: ReturnType<typeof getCollectionProps>;
  labels: string[];
  errors?: ListOfErrors;
  className?: string;
  renderItem?: (props: RadioFieldRenderItemProps) => React.ReactNode;
}) {
  const fallbackId = useId();
  const errorId = errors?.length ? `${groupProps.id ?? fallbackId}-error` : undefined;

  return (
    <div className={className}>
      <RadioGroup
        id={groupProps.id ?? fallbackId}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...groupProps}
      >
        {collectionProps.map((prop, index) => {
          const label = labels[index] ?? '-';

          // Remove the other properties that are not needed for the radio item
          const { name, type, key, ...radioProps } = prop;

          if (renderItem) {
            return (
              <React.Fragment key={prop.key ?? index}>
                {renderItem({ radioProps, label, index, fullProp: prop })}
              </React.Fragment>
            );
          }

          return (
            <div key={prop.key ?? index} className="flex items-center gap-1">
              <RadioItem {...radioProps}>
                <RadioIndicator />
              </RadioItem>
              <Label htmlFor={prop.id}>{label}</Label>
            </div>
          );
        })}
      </RadioGroup>

      {errorId ? (
        <div className="px-4 pb-3 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      ) : (
        <div className="mb-2" />
      )}
    </div>
  );
}
